import React, { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
  ConnectAccountManagement,
  ConnectComponentsProvider,
  ConnectDocuments,
  ConnectPayments,
  ConnectPayouts,
  ConnectPayoutsList,
} from '@stripe/react-connect-js';
import { IconInfoCircle } from '@tabler/icons-react';
import PageHeader from 'components/PageHeader';
import ConnectAccountStatusBadge from 'components/status-badges/ConnectAccountStatusBadge';
import config from 'config';
import { useAuth } from 'context/auth-context';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import useAccountLink from 'hooks/stripe/useAccountLink';
import useStripeConnectAccount from 'hooks/stripe/useStripeConnectAccount';
import { AccountSession } from 'types';
import { client } from 'utils/api-client';

// Where a Standard account holder manages their own details. Not environment-specific: the same
// dashboard serves test and live mode via its own mode toggle.
const STRIPE_DASHBOARD_URL = 'https://dashboard.stripe.com/';

const StripeDashboard = (): JSX.Element => {
  const theme = useMantineTheme();
  const { session } = useAuth();
  const { trackStripeDashboardViewed } = useStaffTracking();

  // The staff carve-out: staff holding StripeDashboardView get the read/operational views,
  // while payout-schedule edits and account/business information stay with the owner. The
  // backend enforces this on the account session and the account-link endpoint; the page has
  // to match, or staff see controls that only produce errors.
  const isOwner = session?.role === 'owner';

  // Demo gyms have no Stripe Connect account at all, so there is nothing for this page to show
  // and every request it would make is doomed. Handled here rather than in the route map: the
  // menu entry stays visible and this page says why it is empty, which keeps the account-shape
  // conditionals out of getStaffRouteMap.
  const isDemoGym = !!session?.demoGym;

  const {
    data: connectAccountData,
    isLoading: connectAccountIsLoading,
    isSuccess: connectAccountIsSuccess,
  } = useStripeConnectAccount({ enabled: !isDemoGym });

  // Stripe only issues account_update links for accounts the platform collects requirements for,
  // and rejects them outright for Standard accounts. A Standard account holder has their own
  // Stripe-hosted dashboard, so they are sent there instead.
  const accountType = connectAccountData?.data.type;
  const supportsAccountUpdateLink = accountType === 'custom';

  const { data: accountLinkData, isLoading: accountLinkIsLoading } = useAccountLink({
    type: 'account_update',
    // account-link is owner-only server-side — requesting it as staff is a guaranteed 403.
    enabled: isOwner && !isDemoGym && supportsAccountUpdateLink,
  });

  // Custom accounts get the platform-hosted update link, which returns them to this page when
  // they are done. Standard accounts get their own Stripe dashboard, which has no return trip.
  //
  // Keyed off 'standard' rather than the absence of Custom, so that neither destination is
  // offered before the account type is known. Undefined for a Custom account while the request
  // is in flight and after it fails, leaving the button disabled — the original bug was a
  // clickable button assigning '' to location.href, which silently reloads the page.
  const opensStripeDashboard = accountType === 'standard';
  const accountUpdateUrl = opensStripeDashboard ? STRIPE_DASHBOARD_URL : accountLinkData?.data.url;

  // The alerts must not send the owner to a button they cannot use — disabled while a Custom
  // account's link is unavailable. Account Management renders for owners of either account type,
  // so it is always a valid fallback instruction.
  const resolveRequirementsHint = accountUpdateUrl
    ? 'Please click the "Update Information" button to satisfy missing requirements.'
    : 'Use the Account Management section below to satisfy the missing requirements.';

  const [stripeConnectInstance] = useState(() => {
    // https://stripe.com/docs/connect/connect-embedded-components/quickstart
    const fetchClientSecret = async (): Promise<string> => {
      // No Connect account means no session to create; skip the call rather than let it 500.
      if (isDemoGym) return '';
      try {
        const response = await client.post<AccountSession>(`/stripe/account-session/`);
        return response.data.clientSecret;
      } catch (error) {
        return '';
      }
    };
    return loadConnectAndInitialize({
      publishableKey: config.STRIPE_PUBLIC_KEY,
      fetchClientSecret: fetchClientSecret,
      appearance: {
        variables: {
          fontFamily: theme.fontFamily,
          colorPrimary: theme.colors.kiloBlue[9],
        },
      },
    });
  });

  // Fires once the connect account has loaded, so the account type comes from the account
  // itself rather than the session payload.
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (hasTrackedView.current || !connectAccountIsSuccess || !connectAccountData?.data) return;
    hasTrackedView.current = true;
    trackStripeDashboardViewed({
      stripe_account_type: connectAccountData.data.type,
      is_owner: !!isOwner,
    });
  }, [trackStripeDashboardViewed, connectAccountIsSuccess, connectAccountData?.data, isOwner]);

  if (isDemoGym) {
    return (
      <>
        <PageHeader>
          <Title order={1}>Stripe Dashboard</Title>
        </PageHeader>
        <Box p='xl'>
          <Alert variant='light' color='blue' title='Not available for demo gyms' icon={<IconInfoCircle />}>
            Demo gyms are not connected to Stripe, so there are no payments, payouts or documents to show
            here. This page becomes available once the gym has a Stripe account.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <PageHeader>
        <Title order={1}>Stripe Dashboard</Title>
      </PageHeader>
      <Box sx={{ overflowY: 'scroll', flexGrow: 1 }}>
        <LoadingOverlay visible={connectAccountIsLoading || accountLinkIsLoading} />
        <Stack p='xl'>
          <Paper shadow='xl' radius='md' withBorder p='xl'>
            <Stack spacing='md'>
              <Group position='apart'>
                <Title order={2}>Summary</Title>
                {isOwner && !!accountType && (
                  <Button
                    // Disabled rather than hidden when there is no link: while a Custom account's
                    // request is in flight, and if it fails. The early return keeps the invariant
                    // even if this prop is ever dropped — nothing may navigate to an empty string.
                    disabled={!accountUpdateUrl}
                    title={
                      opensStripeDashboard
                        ? 'Opens your Stripe dashboard in a new tab'
                        : accountUpdateUrl
                        ? undefined
                        : "Temporarily unavailable — couldn't reach Stripe. Try reloading."
                    }
                    onClick={(): void => {
                      if (!accountUpdateUrl) return;
                      if (opensStripeDashboard) {
                        // No return trip from Stripe's own dashboard, so keep this page open.
                        window.open(accountUpdateUrl, '_blank', 'noopener,noreferrer');
                        return;
                      }
                      window.location.href = accountUpdateUrl;
                    }}
                  >
                    Update Information
                  </Button>
                )}
              </Group>
              <Group spacing='lg'>
                <Group spacing='xs'>
                  <Text>Account Status</Text>
                  {connectAccountIsLoading && <Skeleton height={theme.fontSizes.xl} width={120} />}
                  {connectAccountIsSuccess && connectAccountData?.data && (
                    <ConnectAccountStatusBadge status={connectAccountData.data.status} />
                  )}
                </Group>
                <Group spacing='xs'>
                  <Text>Charges</Text>
                  {connectAccountIsLoading && <Skeleton height={theme.fontSizes.xl} width={120} />}
                  {connectAccountIsSuccess && connectAccountData?.data && (
                    <Text weight='bold'>
                      {connectAccountData.data.chargesEnabled ? 'Enabled' : 'Disabled'}
                    </Text>
                  )}
                </Group>
                <Group spacing='xs'>
                  <Text>Payouts</Text>
                  {connectAccountIsLoading && <Skeleton height={theme.fontSizes.xl} width={120} />}
                  {connectAccountIsSuccess && connectAccountData?.data && (
                    <Text weight='bold'>
                      {connectAccountData.data.payoutsEnabled ? 'Enabled' : 'Disabled'}
                    </Text>
                  )}
                </Group>
              </Group>
              {connectAccountIsSuccess &&
                connectAccountData?.data &&
                connectAccountData.data.status === 'Restricted' && (
                  <Alert variant='light' color='red' title='Action Required' icon={<IconInfoCircle />}>
                    {!isOwner
                      ? 'This account is restricted and is unable to make charges and/or receive payouts. Ask the gym owner to satisfy the missing requirements.'
                      : `Your account is restricted and is unable to make charges and/or receive payouts. ${resolveRequirementsHint}`}
                  </Alert>
                )}
              {connectAccountIsSuccess &&
                connectAccountData?.data &&
                connectAccountData.data.status === 'Restricted soon' && (
                  <Alert variant='light' color='red' title='Action Required' icon={<IconInfoCircle />}>
                    {!isOwner
                      ? 'This account will be restricted soon and unable to make charges and/or receive payouts. Ask the gym owner to satisfy the missing requirements.'
                      : `Your account will be restricted soon and unable to make charges and/or receive payouts. ${resolveRequirementsHint}`}
                  </Alert>
                )}
            </Stack>
          </Paper>
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <Paper shadow='xl' radius='md' withBorder p='xl'>
              <Stack spacing='md'>
                <Title order={2}>Payouts</Title>
                <Box px='md' pt='md'>
                  {/* Staff get the read-only list. The full payouts component can collect
                      external accounts, which would let a staff member re-point where the
                      gym's payouts land — owner-only per the carve-out. The account session
                      enables only the matching component for each role. */}
                  {isOwner ? <ConnectPayouts /> : <ConnectPayoutsList />}
                </Box>
              </Stack>
            </Paper>
            <Paper shadow='xl' radius='md' withBorder p='xl'>
              <Stack spacing='md'>
                <Title order={2}>Payments</Title>
                <Box px='md'>
                  <ConnectPayments />
                </Box>
              </Stack>
            </Paper>
            {/* Owner-only: these are the connected account's tax invoices, including 1099
                forms carrying the business's taxpayer identification number and gross
                earnings. The account session disables the component for staff, so rendering
                it for them would only surface an error. */}
            {isOwner && (
              <Paper shadow='xl' radius='md' withBorder p='xl'>
                <Stack spacing='md'>
                  <Title order={2}>Documents</Title>
                  <Box px='md'>
                    <ConnectDocuments />
                  </Box>
                </Stack>
              </Paper>
            )}
            {/* Owner-only per the staff carve-out — the component is disabled in a staff
                account session, so rendering it for staff would only surface an error. */}
            {isOwner && (
              <Paper shadow='xl' radius='md' withBorder p='xl'>
                <Stack spacing='md'>
                  <Title order={2}>Account Management</Title>
                  <Box px='md'>
                    <ConnectAccountManagement />
                  </Box>
                </Stack>
              </Paper>
            )}
          </ConnectComponentsProvider>
        </Stack>
      </Box>
    </>
  );
};

export default StripeDashboard;
